import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TextareaModule } from 'primeng/textarea';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TenantService } from '@/cloud/services/tenant.service';
import { AppVersionService } from '@/cloud/services/app-version.service';
import { TranslationService } from '@/layout/service/translation.service';
import { CapturedScreenshot } from '@/cloud/services/screenshot.service';
import { ScreenshotAttachComponent } from '@/cloud/components/screenshot-attach/screenshot-attach.component';
import { errorDetail, githubErrorCode } from '@/cloud/models/github.models';
import {
    BUG_IMPACTS,
    FEATURE_IMPORTANCES,
    PORTAL_FEEDBACK_MAX_FIELD,
    PORTAL_FEEDBACK_MAX_TITLE,
    PortalFeedbackKind,
    PortalFeedbackPageContext,
    PortalFeedbackReport,
    buildPortalFeedbackRequest,
    capturePageContext
} from '@/cloud/models/portal-feedback.models';

type Field = { key: string; label: string; placeholder: string; rows: number };

/** A new report, or more information for one the user sent earlier. */
export type PortalFeedbackMode = 'new' | 'update';

/** What the dialog shows after sending: the issue number, and its link for super admins. */
export interface PortalFeedbackSent {
    number: number;
    htmlUrl: string | null;
    update: boolean;
}

/**
 * Top bar "Feedback" button: report a bug or request a feature. The report becomes a GitHub issue for the
 * proAV.Net team, from the bug or feature template, with the page the user was on, its URL, the tenant and
 * who sent it. Hidden when the server has no feedback credential. Drafts survive closing the dialog until sent.
 */
@Component({
    selector: 'app-portal-feedback',
    standalone: true,
    imports: [FormsModule, ButtonModule, DialogModule, InputTextModule, MenuModule, MessageModule, SelectModule, SelectButtonModule, SkeletonModule, TextareaModule, ScreenshotAttachComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (enabled()) {
            <button type="button" class="layout-topbar-action" (click)="menu.toggle($event)" [title]="t()('portalFeedback.button')" [attr.aria-label]="t()('portalFeedback.button')" aria-haspopup="menu">
                <i class="pi pi-megaphone" aria-hidden="true"></i>
            </button>
            <p-menu #menu [model]="menuItems()" [popup]="true" appendTo="body" />

            <p-dialog [header]="header()" [visible]="visible()" (visibleChange)="$event ? visible.set(true) : close()" [modal]="true" [style]="{ width: '42rem', maxWidth: '95vw' }" appendTo="body" [draggable]="false">
                @if (sent(); as result) {
                    <div class="feedback-sent">
                        <i class="pi pi-check-circle" aria-hidden="true"></i>
                        <p>{{ t()(result.update ? 'portalFeedback.update.sent' : 'portalFeedback.sent').replace('{number}', '' + result.number) }}</p>
                        <small>{{ t()(result.update ? 'portalFeedback.update.sentDetail' : 'portalFeedback.sentDetail') }}</small>
                    </div>
                } @else {
                    <div class="feedback-form">
                        @if (reports()?.length) {
                            <p-selectbutton
                                class="feedback-mode"
                                [options]="modeOptions()"
                                optionLabel="label"
                                optionValue="value"
                                [ngModel]="mode()"
                                (ngModelChange)="$event && mode.set($event)"
                                [allowEmpty]="false"
                                [disabled]="sending()"
                                [attr.aria-label]="t()('portalFeedback.mode')"
                            />
                        } @else if (reportsLoading()) {
                            <p-skeleton width="20rem" height="2.4rem" borderRadius="0.5rem" [attr.aria-label]="t()('portalFeedback.update.loading')" />
                        }

                        @if (mode() === 'update') {
                            <p class="feedback-form__intro">{{ t()('portalFeedback.update.intro') }}</p>
                            <div class="field">
                                <label for="feedback-report">{{ t()('portalFeedback.update.report') }} <span class="required" aria-hidden="true">*</span></label>
                                <p-select
                                    inputId="feedback-report"
                                    [options]="reports() ?? []"
                                    optionValue="number"
                                    optionLabel="title"
                                    [ngModel]="selectedReport()"
                                    (ngModelChange)="selectedReport.set($event)"
                                    [filter]="(reports()?.length ?? 0) > 6"
                                    filterBy="title"
                                    [disabled]="sending()"
                                    appendTo="body"
                                    styleClass="w-full"
                                >
                                    <ng-template #selectedItem let-report>
                                        @if (report) {
                                            <span class="report-option">
                                                <i [class]="kindIcon(report.kind)" aria-hidden="true"></i>
                                                <span class="report-option__title">#{{ report.number }} · {{ report.title }}</span>
                                            </span>
                                        }
                                    </ng-template>
                                    <ng-template #item let-report>
                                        <span class="report-option">
                                            <i [class]="kindIcon(report.kind)" aria-hidden="true"></i>
                                            <span class="report-option__title">#{{ report.number }} · {{ report.title }}</span>
                                            <small>{{ updatedLabel(report.updatedUtc) }}</small>
                                        </span>
                                    </ng-template>
                                </p-select>
                            </div>
                            <div class="field">
                                <label for="feedback-update">{{ t()('portalFeedback.update.text') }} <span class="required" aria-hidden="true">*</span></label>
                                <textarea
                                    id="feedback-update"
                                    pTextarea
                                    [rows]="4"
                                    [autoResize]="true"
                                    [maxlength]="maxField"
                                    [placeholder]="t()('portalFeedback.update.textPlaceholder')"
                                    [ngModel]="updateText()"
                                    (ngModelChange)="updateText.set($event ?? '')"
                                    [disabled]="sending()"
                                ></textarea>
                            </div>
                            <small class="feedback-form__hint">{{ t()('portalFeedback.markdownHint') }}</small>
                        } @else {
                        <p-selectbutton
                            [options]="kindOptions()"
                            optionLabel="label"
                            optionValue="value"
                            [ngModel]="kind()"
                            (ngModelChange)="$event && kind.set($event)"
                            [allowEmpty]="false"
                            [disabled]="sending()"
                            [attr.aria-label]="t()('portalFeedback.type')"
                        />
                        <p class="feedback-form__intro">{{ t()(kind() === 'bug' ? 'portalFeedback.bug.intro' : 'portalFeedback.feature.intro') }}</p>

                        <div class="field">
                            <label for="feedback-title">{{ t()('portalFeedback.title') }} <span class="required" aria-hidden="true">*</span></label>
                            <input
                                id="feedback-title"
                                type="text"
                                pInputText
                                [maxlength]="maxTitle"
                                [placeholder]="t()('portalFeedback.' + kind() + '.titlePlaceholder')"
                                [ngModel]="draft()['title'] ?? ''"
                                (ngModelChange)="setField('title', $event)"
                                [disabled]="sending()"
                                required
                            />
                        </div>

                        @for (field of fields(); track field.key) {
                            <div class="field">
                                <label [for]="'feedback-' + field.key">
                                    {{ field.label }}
                                    @if (field.key === 'summary') {
                                        <span class="required" aria-hidden="true">*</span>
                                    }
                                </label>
                                <textarea
                                    [id]="'feedback-' + field.key"
                                    pTextarea
                                    [rows]="field.rows"
                                    [autoResize]="true"
                                    [maxlength]="maxField"
                                    [placeholder]="field.placeholder"
                                    [ngModel]="draft()[field.key] ?? ''"
                                    (ngModelChange)="setField(field.key, $event)"
                                    [disabled]="sending()"
                                ></textarea>
                            </div>
                        }

                        <div class="field">
                            <label for="feedback-choice">{{ t()(kind() === 'bug' ? 'portalFeedback.bug.impact' : 'portalFeedback.feature.importance') }}</label>
                            <p-select
                                inputId="feedback-choice"
                                [options]="choiceOptions()"
                                optionLabel="label"
                                optionValue="value"
                                [ngModel]="choice()"
                                (ngModelChange)="setChoice($event)"
                                [showClear]="true"
                                [placeholder]="t()('portalFeedback.choose')"
                                [disabled]="sending()"
                                appendTo="body"
                                styleClass="w-full"
                            />
                        </div>
                        <small class="feedback-form__hint">{{ t()('portalFeedback.markdownHint') }}</small>
                        }

                        <app-screenshot-attach [(value)]="screenshot" [disabled]="sending()" />

                        @if (context(); as ctx) {
                            <details class="feedback-context">
                                <summary>{{ t()('portalFeedback.included') }}</summary>
                                <dl>
                                    <dt>{{ t()('portalFeedback.context.user') }}</dt>
                                    <dd>{{ userLabel() }}</dd>
                                    @if (tenantName()) {
                                        <dt>{{ t()('portalFeedback.context.tenant') }}</dt>
                                        <dd>{{ tenantName() }}</dd>
                                    }
                                    <dt>{{ t()('portalFeedback.context.page') }}</dt>
                                    <dd>
                                        {{ ctx.pageTitle || '—' }}
                                        @if (ctx.route) {
                                            <code>{{ ctx.route }}</code>
                                        }
                                    </dd>
                                    <dt>{{ t()('portalFeedback.context.url') }}</dt>
                                    <dd>
                                        <code class="feedback-context__url">{{ ctx.url }}</code>
                                    </dd>
                                    <dt>{{ t()('portalFeedback.context.version') }}</dt>
                                    <dd>{{ ctx.uiVersion || '—' }}</dd>
                                    <dt>{{ t()('portalFeedback.context.browser') }}</dt>
                                    <dd>{{ browser }} · {{ ctx.viewport }} · {{ ctx.language }} · {{ ctx.timeZone }}</dd>
                                </dl>
                                <small>{{ t()('portalFeedback.redactionNote') }}</small>
                            </details>
                        }

                        @if (error(); as message) {
                            <p-message severity="error">{{ message }}</p-message>
                        }
                    </div>
                }
                <ng-template #footer>
                    @if (sent(); as result) {
                        @if (result.htmlUrl) {
                            <a pButton [text]="true" [href]="result.htmlUrl" target="_blank" rel="noopener">
                                <i pButtonIcon class="pi pi-github" aria-hidden="true"></i>
                                <span pButtonLabel>{{ t()('portalFeedback.openOnGitHub') }}</span>
                            </a>
                        }
                        <p-button [label]="t()('portalFeedback.close')" (onClick)="close()" />
                    } @else {
                        <p-button [label]="t()('portalFeedback.cancel')" [text]="true" (onClick)="close()" [disabled]="sending()" />
                        <p-button [label]="t()('portalFeedback.send')" icon="pi pi-send" (onClick)="send()" [disabled]="!canSend()" [loading]="sending()" />
                    }
                </ng-template>
            </p-dialog>
        }
    `,
    styles: [
        `
            /* Only the button joins the top-bar row: the menu and dialog render in <body>, but their host
               elements would otherwise become extra flex items and widen the gap after this button. */
            :host {
                display: inline-flex;
            }
            .feedback-form {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-3, 0.75rem);
            }
            .feedback-form__intro,
            .feedback-form__hint {
                margin: 0;
                color: var(--text-color-secondary);
                font-size: 0.875rem;
            }
            .field {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-1, 0.25rem);
            }
            .field label {
                font-weight: 600;
                font-size: 0.875rem;
            }
            .required {
                color: var(--p-red-500, #ef4444);
            }
            .feedback-context {
                border: 1px solid var(--p-content-border-color, var(--surface-border));
                border-radius: 0.5rem;
                padding: var(--app-space-2, 0.5rem) var(--app-space-3, 0.75rem);
                font-size: 0.875rem;
            }
            .feedback-context summary {
                cursor: pointer;
                font-weight: 600;
            }
            .feedback-context dl {
                display: grid;
                grid-template-columns: max-content 1fr;
                gap: 0.25rem 1rem;
                margin: 0.75rem 0 0.5rem;
            }
            .feedback-context dt {
                color: var(--text-color-secondary);
            }
            .feedback-context dd {
                margin: 0;
                overflow-wrap: anywhere;
            }
            .feedback-context code {
                margin-inline-start: 0.5rem;
                font-size: 0.8rem;
            }
            .feedback-context__url {
                margin-inline-start: 0 !important;
            }
            .feedback-context small {
                color: var(--text-color-secondary);
            }
            .feedback-mode {
                align-self: flex-start;
            }
            .report-option {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                min-width: 0;
            }
            .report-option i {
                color: var(--text-color-secondary);
            }
            .report-option__title {
                flex: 1;
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .report-option small {
                color: var(--text-color-secondary);
                white-space: nowrap;
            }
            .feedback-sent {
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                gap: 0.5rem;
                padding: 1.5rem 0;
            }
            .feedback-sent i {
                font-size: 2.5rem;
                color: var(--p-green-500, #22c55e);
            }
            .feedback-sent p {
                margin: 0;
                font-weight: 600;
            }
            .feedback-sent small {
                color: var(--text-color-secondary);
            }
        `
    ]
})
export class PortalFeedbackComponent {
    private api = inject(CloudApiService);
    private tenants = inject(TenantService);
    private version = inject(AppVersionService);
    private router = inject(Router);
    private translation = inject(TranslationService);
    readonly t = this.translation.t;
    private readonly language = this.translation.currentLanguage;

    readonly maxTitle = PORTAL_FEEDBACK_MAX_TITLE;
    readonly maxField = PORTAL_FEEDBACK_MAX_FIELD;
    readonly browser = PortalFeedbackComponent.browserName(navigator.userAgent);

    readonly enabled = signal(false);
    readonly visible = signal(false);
    readonly kind = signal<PortalFeedbackKind>('bug');
    readonly sending = signal(false);
    readonly error = signal<string | null>(null);
    readonly sent = signal<PortalFeedbackSent | null>(null);
    readonly mode = signal<PortalFeedbackMode>('new');
    /** The user's open reports; null until loaded (lazily, when the dialog opens). */
    readonly reports = signal<PortalFeedbackReport[] | null>(null);
    readonly reportsLoading = signal(false);
    readonly selectedReport = signal<number | null>(null);
    readonly updateText = signal('');
    readonly context = signal<PortalFeedbackPageContext | null>(null);
    /** Opt-in capture; uploaded only when the report is sent. */
    readonly screenshot = signal<CapturedScreenshot | null>(null);
    /** The upload of the current capture, so a failed send does not upload it twice. */
    private uploaded: { shot: CapturedScreenshot; id: string } | null = null;

    /** One draft per kind, so switching type does not lose what was typed. */
    private readonly drafts = signal<Record<PortalFeedbackKind, Partial<Record<string, string>>>>({ bug: {}, feature: {} });
    private readonly choices = signal<Record<PortalFeedbackKind, string | null>>({ bug: null, feature: null });

    readonly draft = computed(() => this.drafts()[this.kind()]);
    readonly choice = computed(() => this.choices()[this.kind()]);

    readonly header = computed(() =>
        this.t()(this.mode() === 'update' ? 'portalFeedback.update.header' : this.kind() === 'bug' ? 'portalFeedback.reportBug' : 'portalFeedback.requestFeature')
    );

    readonly modeOptions = computed(() => [
        { label: this.t()('portalFeedback.mode.new'), value: 'new' },
        { label: this.t()('portalFeedback.mode.update'), value: 'update' }
    ]);

    readonly menuItems = computed<MenuItem[]>(() => {
        const t = this.t();
        return [
            {
                label: t('portalFeedback.menuHeader'),
                items: [
                    { label: t('portalFeedback.reportBug'), icon: 'far fa-bug', command: () => this.open('bug') },
                    { label: t('portalFeedback.requestFeature'), icon: 'far fa-lightbulb-on', command: () => this.open('feature') }
                ]
            }
        ];
    });

    readonly kindOptions = computed(() => [
        { label: this.t()('portalFeedback.bugOption'), value: 'bug' },
        { label: this.t()('portalFeedback.featureOption'), value: 'feature' }
    ]);

    readonly fields = computed<Field[]>(() => {
        const t = this.t();
        const field = (key: string, rows: number): Field => ({ key, rows, label: t(`portalFeedback.${this.kind()}.${key}`), placeholder: t(`portalFeedback.${this.kind()}.${key}Placeholder`) });
        return this.kind() === 'bug' ? [field('summary', 3), field('stepsToReproduce', 4), field('expectedBehaviour', 2), field('actualBehaviour', 2)] : [field('summary', 3), field('problem', 3), field('proposedSolution', 3)];
    });

    readonly choiceOptions = computed(() => {
        const t = this.t();
        return this.kind() === 'bug' ? BUG_IMPACTS.map((value) => ({ value, label: t(`portalFeedback.impact.${value}`) })) : FEATURE_IMPORTANCES.map((value) => ({ value, label: t(`portalFeedback.importance.${value}`) }));
    });

    readonly canSend = computed(() => {
        if (this.sending()) return false;
        if (this.mode() === 'update') return this.selectedReport() !== null && !!this.updateText().trim();
        return !!this.draft()['title']?.trim() && !!this.draft()['summary']?.trim();
    });

    readonly userLabel = computed(() => {
        const user = this.api.currentUser();
        if (!user) return '—';
        const name = user.displayName || user.username;
        return user.email ? `${name} (${user.email})` : name;
    });

    readonly tenantName = computed(() => this.tenants.selectedTenant()?.name ?? null);

    private statusRequested = false;

    constructor() {
        // Ask once per session whether the server can raise reports; a failure leaves the button hidden.
        effect(() => {
            if (!this.api.currentUser() || this.statusRequested) return;
            this.statusRequested = true;
            untracked(() =>
                this.api
                    .getPortalFeedbackStatus()
                    .then((status) => this.enabled.set(!!status?.enabled))
                    .catch(() => this.enabled.set(false))
            );
        });
    }

    open(kind: PortalFeedbackKind): void {
        this.kind.set(kind);
        this.error.set(null);
        this.sent.set(null);
        // Captured now, while the user is still on the page the report is about.
        this.context.set(capturePageContext({ root: this.router.routerState.snapshot.root, namespaceId: this.tenants.selectedTenantId(), uiVersion: this.version.currentVersion || null }));
        // A capture belongs to the page it was taken on.
        this.screenshot.set(null);
        this.mode.set('new');
        this.visible.set(true);
        void this.loadReports();
    }

    /** Lists the user's open reports so they can add to one; failures just leave "Add to an earlier report" out. */
    async loadReports(): Promise<void> {
        if (this.reportsLoading()) return;
        this.reportsLoading.set(true);
        try {
            const reports = await this.api.listMyPortalFeedback();
            this.reports.set(reports);
            // Keep the choice while it is still open; otherwise the most recently updated report.
            if (!reports.some((r) => r.number === this.selectedReport())) this.selectedReport.set(reports[0]?.number ?? null);
            if (!reports.length && this.mode() === 'update') this.mode.set('new');
        } catch {
            this.reports.set([]);
            if (this.mode() === 'update') this.mode.set('new');
        } finally {
            this.reportsLoading.set(false);
        }
    }

    kindIcon(kind: PortalFeedbackKind): string {
        return kind === 'feature' ? 'far fa-lightbulb-on' : 'far fa-bug';
    }

    /** "Updated 3 hours ago", in the portal's language. */
    updatedLabel(value: string, now = Date.now()): string {
        const time = Date.parse(value);
        if (Number.isNaN(time)) return '';
        const seconds = Math.round((time - now) / 1000);
        const [amount, unit]: [number, Intl.RelativeTimeFormatUnit] =
            Math.abs(seconds) < 60 ? [seconds, 'second'] : Math.abs(seconds) < 3600 ? [Math.round(seconds / 60), 'minute'] : Math.abs(seconds) < 86_400 ? [Math.round(seconds / 3600), 'hour'] : [Math.round(seconds / 86_400), 'day'];
        let relative: string;
        try {
            relative = new Intl.RelativeTimeFormat(this.language(), { numeric: 'auto' }).format(amount, unit);
        } catch {
            relative = new Date(time).toLocaleDateString();
        }
        return this.t()('portalFeedback.update.updated').replace('{when}', relative);
    }

    close(): void {
        if (this.sending()) return;
        this.visible.set(false);
        this.sent.set(null);
    }

    setField(key: string, value: string): void {
        const kind = this.kind();
        this.drafts.update((d) => ({ ...d, [kind]: { ...d[kind], [key]: value ?? '' } }));
    }

    setChoice(value: string | null): void {
        const kind = this.kind();
        this.choices.update((c) => ({ ...c, [kind]: value ?? null }));
    }

    async send(): Promise<void> {
        if (this.mode() === 'update') return this.sendUpdate();
        const context = this.context();
        if (!this.canSend() || !context) return;
        const kind = this.kind();
        this.sending.set(true);
        this.error.set(null);
        try {
            const screenshotId = await this.uploadScreenshot(context.namespaceId);
            if (screenshotId === undefined) return;
            const result = await this.api.submitPortalFeedback({ ...buildPortalFeedbackRequest(kind, this.draft(), this.choice(), context), screenshotId });
            this.sent.set({ number: result.number, htmlUrl: result.htmlUrl, update: false });
            // The new report can be added to next time.
            this.reports.set(null);
            this.drafts.update((d) => ({ ...d, [kind]: {} }));
            this.choices.update((c) => ({ ...c, [kind]: null }));
            this.screenshot.set(null);
            this.uploaded = null;
        } catch (err) {
            this.error.set(this.errorMessage(err));
        } finally {
            this.sending.set(false);
        }
    }

    private async sendUpdate(): Promise<void> {
        const context = this.context();
        const number = this.selectedReport();
        if (!this.canSend() || !context || number === null) return;
        this.sending.set(true);
        this.error.set(null);
        try {
            const screenshotId = await this.uploadScreenshot(context.namespaceId);
            if (screenshotId === undefined) return;
            const result = await this.api.addPortalFeedbackUpdate(number, { text: this.updateText().trim(), context, screenshotId });
            this.sent.set({ number: result.number, htmlUrl: result.commentUrl, update: true });
            this.updateText.set('');
            this.screenshot.set(null);
            this.uploaded = null;
            this.reports.set(null);
        } catch (err) {
            this.error.set(this.errorMessage(err));
        } finally {
            this.sending.set(false);
        }
    }

    /** The attached screenshot's id, null when none is attached, or undefined (with the error shown) when the upload failed. */
    private async uploadScreenshot(namespaceId: string | null): Promise<string | null | undefined> {
        const shot = this.screenshot();
        if (!shot) return null;
        if (this.uploaded?.shot === shot) return this.uploaded.id;
        try {
            const { id } = await this.api.uploadFeedbackScreenshot(shot.blob, namespaceId);
            this.uploaded = { shot, id };
            return id;
        } catch {
            this.error.set(this.t()('screenshot.uploadFailed'));
            return undefined;
        }
    }

    private errorMessage(err: unknown): string {
        const code = githubErrorCode(err);
        const known = code ? this.t()(`portalFeedback.error.${code}`) : null;
        if (known && known !== `portalFeedback.error.${code}`) return known;
        return errorDetail(err, this.t()('portalFeedback.error.generic'));
    }

    /** A short browser name for the preview; the full user agent is added by the server. */
    static browserName(userAgent: string): string {
        if (/Edg\//.test(userAgent)) return 'Edge';
        if (/Firefox\//.test(userAgent)) return 'Firefox';
        if (/Chrome\//.test(userAgent)) return 'Chrome';
        if (/Safari\//.test(userAgent)) return 'Safari';
        return 'Browser';
    }
}
